import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Box, Grid, Skeleton } from "@mui/material";

import Styles from "./LedgerPageDashboardActive.module.css";
import DetailsHeader from "../DetailsHeader/DetailsHeader";
import { Accordion, AccordionDetails } from "../../../commons/Accordion";
import LegerViewType from "../LedgerViewContent/LegerViewType";
import LedgerFinalTc from "../LedgerViewContent/LedgerFinalTc";
import LedgerUploadDocs from "../LedgerViewContent/ledgerUploadDocs";
import TermsCondition from "../../Modals/ConGen/TermsCondition/TermsCondition";
import AcceptTermsCondition from "../../Modals/ConGen/AcceptTermsCondition/AcceptTermsCondition";

import {
  handleAcceptT_C,
  handleInitEditT_C,
  handleRejectAllT_C,
} from "../../../Services/BuySell/Creation";
import { withdrawTerms } from "../../../Services/BuySell/Listing";
import {
  ErrorMessage,
  SuccessMessage,
} from "../../../commons/Toast/ErrorMessage";
import {
  fetchActiveConnection,
  fetchMainScreenActiveConnection,
  fetchSelfCapacity,
  fetchSelfCapacityById,
} from "../../../Redux/Slices/CapacitySlice";
import { formatDate } from "../../../helpers/dateFormat";
import emptySelfListing from "../../../assets/images/emptyActive.png";
import { toast } from "react-toastify";
import ConfirmationModal from "../../../commons/ConfirmationModal/ConfirmationModal";
import EditTermsConnection from "../../Modals/ConGen/EditTermsConnection/EditTermsConnection";

// Constants
export const USER_TYPES = {
  CONSUMER: "consumer",
  GENERATOR: "generator",
};

const ACTIONS = {
  FINALIZE: "Finalize T&C",
  ACCEPT: "Approve T&C",
  VIEW: "View T&C",
  WAITING: "Waiting...",
  PROCEED: "Proceed to Bilateral",
  WITHDRAW: "Withdraw T&C",
  REJECTALL: "Reject All",
};

// Utility Components
const EmptyState = () => (
  <div className={Styles.emptyState}>
    <img
      style={{ marginBottom: "10px" }}
      src={emptySelfListing}
      alt="No Available Listings"
    />
    <h2>No Active Connections</h2>
    <p>
      You haven't accepted any requests yet. Once you do, they'll appear here so
      you can proceed with Finalizing terms and conditions.
    </p>
  </div>
);

const CardLoader = () => (
  <Box
    sx={{
      border: "1px solid #eee",
      borderRadius: "8px",
      p: 2,
      mb: 2,
      boxShadow: "0px 2px 6px rgba(0,0,0,0.05)",
    }}
  >
    <Skeleton variant="text" width="40%" height={30} />
    <Skeleton variant="text" width="30%" height={20} />
    <Grid container spacing={2} mt={1}>
      {[2, 2, 2, 3, 3].map((width, index) => (
        <Grid item xs={width} key={index}>
          <Skeleton variant="text" height={25} />
        </Grid>
      ))}
    </Grid>
    <Box mt={2} display="flex" gap={2}>
      {[100, 120, 130].map((width, index) => (
        <Skeleton key={index} variant="rectangular" width={width} height={35} />
      ))}
    </Box>
  </Box>
);

const TableHeader = ({
  user,
  status,
  data,
  isInitiator,
  result,
  matchView,
  hideBucketFill = false,
}) => {
  const showBucket =
    !hideBucketFill &&
    user === USER_TYPES.CONSUMER &&
    (result.someHaveInitiatorType || result.noneHaveInitiatorType) &&
    ["TERMS_INITIATED", "APPROVED", "RECEIVED", "PLACED"].includes(status) &&
    !(
      result.allHaveInitiatorType &&
      user === "generator" &&
      (((result.allAreConsumer ||
        result.allAreGenerator ||
        result.allAreBoth) &&
        user === "consumer") ||
        ((result.allAreConsumer ||
          result.allAreGenerator ||
          result.allAreBoth) &&
          user === "generator"))
    );

  return (
    <table className={Styles.ledgerTableHeading}>
      <thead>
        <tr>
          <th
            style={{
              textAlign: "left",
              paddingLeft: "38px",
              width: showBucket ? "16.6%" : "20%",
            }}
            className=""
          >
            Self Requisition
          </th>
          <th style={{ width: showBucket ? "16.6%" : "20%" }} className="">
            Quantity
          </th>
          <th style={{ width: showBucket ? "16.6%" : "20%" }} className="">
            Energy Rate
          </th>
          <th style={{ width: showBucket ? "16.6%" : "20%" }} className="">
            Date Range
          </th>
          <th
            style={{
              textAlign: user === "consumer" && showBucket ? "right" : "center",
              width: showBucket ? "16.6%" : "20%",
            }}
            className=""
          >
            Time Range
          </th>
          {showBucket && (
            <th
              style={{
                textAlign: "right",
                paddingRight: "38px",
                width: showBucket ? "16.6%" : "20%",
              }}
              className=""
            >
              Bucket Fill
            </th>
          )}
        </tr>
      </thead>
    </table>
  );
};

const useSelectors = () => {
  const selfCapacityLoading = useSelector(
    (state) => state?.CapacitySlice?.fetchSelfCapacityLoading,
  );
  const activeConnectionLoading = useSelector(
    (state) => state?.CapacitySlice?.fetchActiveConnectionLoading,
  );
  const onGoingData = useSelector(
    (state) => state?.CapacitySlice?.activeConnection,
  );
  const list = useSelector((state) => state?.CapacitySlice?.list);
  const userId = useSelector(
    (state) => state.persistedReducer.user.user.userId,
  );

  return {
    selfCapacityLoading,
    activeConnectionLoading,
    onGoingData,
    list,
    userId,
  };
};

// Utility Functions
const getRoleDataKey = (user) =>
  user === USER_TYPES.CONSUMER ? "capacityConsumer" : "capacityGenerator";

const getAllContractIds = (data) => {
  const ids = [];

  // Top-level contractId
  if (data?.capacityConsumer?.contractId !== undefined) {
    ids.push(data.capacityConsumer.contractId);
  }
  if (data?.capacityGenerator?.contractId !== undefined) {
    ids.push(data.capacityGenerator.contractId);
  }

  // From matches
  if (Array.isArray(data?.matches)) {
    data.matches.forEach((match) => {
      [...(match.generators || []), ...(match.consumers || [])].forEach(
        (item) => {
          if (item.contractId !== undefined) {
            ids.push(item.contractId);
          }
        },
      );
    });
  }

  return Array.from(new Set(ids));
};

const normalizeTime = (time) => (time === "24:00" ? "23:59" : time);

// ============================================================================
// MINIMAL FIX - Only update transformToPayload function
// Keep all existing logic intact, just fix the ID matching issue
// ============================================================================

const transformToPayload = (
  finalValues,
  user,
  onGoingData,
  listingId,
  selectedBids,
) => {
  const bilateralConnections = [];
  const paymentDetailsMap = new Map();
  const processedSlots = new Set();

  // Create a flexible matching function that works with both old and new IDs
  const isSlotSelected = (
    selfSlotId,
    matchSlotId,
    connectId,
    companyName,
    matchIndex,
  ) => {
    return selectedBids.some((bid) => {
      // Match by uniqueId (format: selfSlotId-matchSlotId or selfSlotId-matchSlotId-matchIndex)
      const uniqueId1 = `${selfSlotId}-${matchSlotId}`;
      const uniqueId2 = `${selfSlotId}-${matchSlotId}-${matchIndex}`;

      // Also match by stable properties (in case IDs changed)
      const stableMatch =
        bid.selfRequisitionIndex == selfSlotId &&
        bid.consumerGeneratorconnectId == connectId &&
        bid.company === companyName;

      return (
        bid.uniqueId === uniqueId1 || bid.uniqueId === uniqueId2 || stableMatch
      );
    });
  };

  Object.keys(finalValues).forEach((selfReqIndex) => {
    const requisitionData = finalValues[selfReqIndex];

    Object.keys(requisitionData).forEach((connectId) => {
      const counterpartyData = requisitionData[connectId];
      const matchData = onGoingData
        ?.find((item) =>
          item.matches?.some((match) => match.selfSlot.id == selfReqIndex),
        )
        ?.matches?.find((match) => match.selfSlot.id == selfReqIndex);

      if (!matchData) return;

      let counterparty;

      if (user === USER_TYPES.CONSUMER) {
        counterparty = matchData?.generators?.find(
          (gen) => gen.connectId == connectId,
        );
      } else if (user === USER_TYPES.GENERATOR) {
        counterparty = matchData?.consumers?.find(
          (con) => con.connectId == connectId,
        );
      }

      if (matchData && counterparty) {
        if (!paymentDetailsMap.has(connectId)) {
          paymentDetailsMap.set(connectId, {
            consumerGeneratorconnectId: parseInt(connectId),
            paymentSecurity: parseFloat(counterpartyData.paymentSecurity || 0),
            initialMargin: parseFloat(counterpartyData.initialMargin || 0),
            emd: parseFloat(counterpartyData.emd || 0),
          });
        }

        // Filter matchSlots to only include those that are selected
        let reqCounter = 1; // Track the req number for this connectId
        counterparty.matchSlots?.forEach((matchSlot, slotIndex) => {
          if (matchSlot.finalizedConnectionValues) {
            const sourceData = matchSlot.finalizedConnectionValues || matchSlot;

            // FIXED: Use matchSlot.id instead of counterpartyData?.id
            const consumerSlotId =
              user === USER_TYPES.CONSUMER
                ? matchData.selfSlot.id
                : matchSlot.id;
            const generatorSlotId =
              user === USER_TYPES.CONSUMER
                ? matchSlot.id
                : matchData.selfSlot.id;

            // In the connection object, prioritize finalized values if available
            const connection = {
              id: sourceData.id,
              consumerGeneratorconnectId: parseInt(connectId),
              consumerSlotId,
              generatorSlotId,
              dateStartAt:
                counterpartyData[`req${reqCounter}_startDate`] ||
                sourceData.dateStartAt,
              dateEndAt:
                counterpartyData[`req${reqCounter}_endDate`] ||
                sourceData.dateEndAt,
              slotStartAt: normalizeTime(
                counterpartyData[`req${reqCounter}_startTime`] ||
                  sourceData.slotStartAt,
              ),
              slotEndAt: normalizeTime(
                counterpartyData[`req${reqCounter}_endTime`] ||
                  sourceData.slotEndAt,
              ),

              quantity: parseFloat(
                counterpartyData[`req${reqCounter}_quantity`] ||
                  sourceData.quantity ||
                  0,
              ),
              energyRateType:
                counterpartyData[`req${reqCounter}_energyRateStr`] ||
                sourceData.energyRateType ||
                "",
              amount:
                (isNaN(counterpartyData[`req${reqCounter}_energyRate`])
                  ? counterpartyData[`req${reqCounter}_energyRate`]?.split(
                      "+",
                    )?.[1]
                    ? Number(
                        counterpartyData[`req${reqCounter}_energyRate`]?.split(
                          "+",
                        )?.[1],
                      )
                    : Number(
                        counterpartyData[`req${reqCounter}_energyRate`]?.split(
                          "-",
                        )?.[1],
                      )
                  : Number(counterpartyData[`req${reqCounter}_energyRate`])) ||
                matchSlot?.landingCost ||
                matchSlot?.sellingCost ||
                0,
              purchaseEnergy:
                Number(counterpartyData[`req${reqCounter}_quantity`]) ||
                sourceData.quantity ||
                0,
            };

            bilateralConnections.push(connection);
            reqCounter++;
          } else {
            // Counterparty is initiator - use finalized or matchSlot values
            const consumerSlotId =
              user === USER_TYPES.CONSUMER
                ? matchData.selfSlot.id
                : matchSlot.id;
            const generatorSlotId =
              user === USER_TYPES.CONSUMER
                ? matchSlot.id
                : matchData.selfSlot.id;

            // Find the matchIndex from the original data structure
            const matchIndex =
              onGoingData?.findIndex((item) =>
                item.matches?.some((m) => m.selfSlot.id == selfReqIndex),
              ) || 0;

            // Check if this slot is selected using flexible matching
            if (
              !isSlotSelected(
                matchData.selfSlot.id,
                matchSlot.id,
                connectId,
                counterparty.companyName,
                matchIndex,
              )
            ) {
              return; // Skip if not selected
            }

            const slotKey = `${consumerSlotId}-${generatorSlotId}-${connectId}`;

            if (processedSlots.has(slotKey)) {
              return;
            }

            processedSlots.add(slotKey);

            const connection = {
              consumerGeneratorconnectId: parseInt(connectId),
              consumerSlotId,
              generatorSlotId,
              dateStartAt:
                counterpartyData[`req${reqCounter}_startDate`] ||
                matchSlot.dateStartAt,
              dateEndAt:
                counterpartyData[`req${reqCounter}_endDate`] ||
                matchSlot.dateEndAt,
              slotStartAt: normalizeTime(
                counterpartyData[`req${reqCounter}_startTime`] ||
                  matchSlot.slotStartAt,
              ),
              slotEndAt: normalizeTime(
                counterpartyData[`req${reqCounter}_endTime`] ||
                  matchSlot.slotEndAt,
              ),
              quantity: parseFloat(
                counterpartyData[`req${reqCounter}_quantity`] || 0,
              ),
              amount:
                (isNaN(counterpartyData[`req${reqCounter}_energyRate`])
                  ? counterpartyData[`req${reqCounter}_energyRate`]?.split(
                      "+",
                    )?.[1]
                    ? Number(
                        counterpartyData[`req${reqCounter}_energyRate`]?.split(
                          "+",
                        )?.[1],
                      )
                    : Number(
                        counterpartyData[`req${reqCounter}_energyRate`]?.split(
                          "-",
                        )?.[1],
                      )
                  : Number(counterpartyData[`req${reqCounter}_energyRate`])) ||
                matchSlot?.landingCost ||
                matchSlot?.sellingCost ||
                0,
              purchaseEnergy:
                matchSlot?.purchaseEnergy || matchSlot?.sellEnergy || 0,
              energyRateType:
                counterpartyData[`req${reqCounter}_energyRateStr`] || "",
            };
            bilateralConnections.push(connection);
            reqCounter++; // Increment for next selected slot
          }
        });
      }
    });
  });

  return {
    userType: user,
    bilateralConnections,
    paymentDetails: Array.from(paymentDetailsMap.values()),
    capacityId: listingId,
  };
};

const getUniqueCapacities = (onGoingData, user, status) => {
  if (status !== "TERMS_ACCEPTED" && status !== "ONGOING") return [];
  const userDataKey = getRoleDataKey(user);
  const map = new Map();

  for (let i = 0; i < onGoingData?.length; i++) {
    const element = onGoingData[i][userDataKey];
    if (element && !map.has(element.id)) {
      map.set(element.id, element);
    }
  }

  return Array.from(map.values());
};

const getUniqueCounterCapacity = (onGoingData, status) => {
  if (status !== "TERMS_ACCEPTED" && status !== "ONGOING") return [];

  const allConnections = [];
  for (let i = 0; i < onGoingData.length; i++) {
    const connections = onGoingData[i]?.bilateralConnections;
    if (Array.isArray(connections)) {
      allConnections.push(...connections);
    }
  }

  return allConnections;
};

// Main Component
const LedgerPageDashboardActive = ({
  viewType,
  setActiveSubTab,
  capacityId,
}) => {
  // State
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(0);
  const location = useLocation();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [isEditTerms, setIsEditTerms] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const { hasApprovedAndPending } = location.state || {};
  const [confirmWithdrawModalOpen, setConfirmWithdrawModalOpen] =
    useState(false);
  const [termsModal, setTermsModal] = useState(false);
  const [approveTermsLoading, setApproveTermsLoading] = useState(false);
  const [termsId, setTermsId] = useState({});
  const [withdrawLoading, setWithdrawLoading] = useState(false);
  const [companySelected, setCompanySelected] = useState([]);
  const [acceptTermsModal, setAcceptTermsModal] = useState(false);
  const [acceptTermsModalData, setAcceptTermsModalData] = useState(false);
  const [contractIds, setContractIds] = useState([]);
  const [selectedBids, setSelectedBids] = useState([]);
  const [requisition, setRequsition] = useState([]);
  const [companyUpdate, setCompanyUpdate] = useState([]);
  const [termsSubmitLoading, setTermsSubmitLoading] = useState(false);
  const [selfReq, setSelfReq] = useState(null);
  const [matchView, setMatchView] = useState(null);
  const [hideBucketFill, setHideBucketFill] = useState(false);
  const [proceedToBilateralLoading, setProceedToBilateralLoading] =
    useState(false);
  const [proceedToBilateralConfirm, setProceedToBilateralConfirm] =
    useState(false);
  const [rejectAllTermsConfirm, setRejectAllTermsConfirm] = useState(false);
  const [editData, setEditData] = useState(null);

  const [editTermsData, setEditTermsData] = useState(null);
  const [editTermsModal, setEditTermsModal] = useState(false);

  // Hooks
  const listingId = capacityId || location?.search?.split("=")[1];
  const user = useSelector(
    (state) => state.persistedReducer.user?.activeRole,
  )?.toLowerCase();
  const dispatch = useDispatch();
  const {
    selfCapacityLoading,
    activeConnectionLoading,
    onGoingData,
    list,
    userId,
  } = useSelectors();

  const self = useSelector((store) => store?.CapacitySlice?.getSelfListingById);

  useEffect(() => {
    dispatch(fetchSelfCapacityById({ user, id: listingId }));
  }, [dispatch, listingId, user]);

  useEffect(() => {
    if (self) {
      setSelfReq(self);
    }
  }, [self]);

  // Computed values
  const roleDataKey = getRoleDataKey(user);
  const activeListingStatus = selfReq?.status;

  const displayActiveText = location.pathname.includes("bilateral")
    ? "Active Connection"
    : "Ongoing Connection";

  // Event Handlers
  const handleAccordionChange = useCallback((val) => {
    setExpanded((prev) => (val === prev ? "" : val));
  }, []);

  const handleTermsClose = useCallback(() => {
    setTermsModal(false);
  }, []);

  const handleAcceptTermsClose = useCallback(() => {
    setAcceptTermsModal(false);
  }, []);

  const handleEditTermsClose = useCallback(() => {
    setEditTermsModal(false);
  }, []);

  // Solution 1: Create a stable unique identifier using business logic properties
  // instead of relying on potentially changing IDs

  // In handleSelectedBids, create a stable identifier:
  const handleSelectedBids = useCallback((data) => {
    // Create stable identifier using properties that don't change
    const stableId = `${data?.selfRequisitionIndex}-${data?.consumerGeneratorconnectId}-${data?.company}`;

    const company = {
      companyName: data?.company,
      selfRequisitionIndex: data?.selfRequisitionIndex,
      consumerGeneratorconnectId: data?.consumerGeneratorconnectId,
      uniqueId: data?.uniqueId,
      stableId: stableId, // Add stable identifier
    };

    if (data.selected === false) {
      setSelectedBids((prev) =>
        prev.filter((ele) => ele.stableId !== stableId),
      );
      setCompanySelected((prev) =>
        prev.filter((ele) => ele.stableId !== stableId),
      );
    } else {
      setSelectedBids((prev) => {
        if (prev.some((b) => b.stableId === stableId)) return prev;
        return [...prev, { ...data, stableId }];
      });

      setCompanySelected((prev) => {
        if (prev.some((ele) => ele.stableId === stableId)) return prev;
        return [...prev, company];
      });
    }
  }, []);

  const handleReq = useCallback((data) => {
    setRequsition(data);
  }, []);

  const checkPayloadType = useCallback(
    (finalValues) => {
      if (!onGoingData[0]?.matches || !Array.isArray(onGoingData[0].matches)) {
        return { type: "finalize", hasFinalize: false, hasAccept: false };
      }

      const matches = onGoingData[0].matches;
      const counterpartyKey =
        user === USER_TYPES.GENERATOR ? "consumers" : "generators";

      let hasWithInitiatorType = false;
      let hasWithoutInitiatorType = false;

      matches.forEach((match) => {
        const counterparties = match[counterpartyKey] || [];

        counterparties.forEach((counterparty) => {
          // Check if connectId exists in finalValues (if finalValues is provided)
          if (
            finalValues &&
            !Object.values(finalValues).some((reqData) =>
              Object.keys(reqData).includes(counterparty.connectId?.toString()),
            )
          ) {
            return; // Skip this counterparty if not in finalValues
          }

          if (counterparty.initiatorType) {
            hasWithInitiatorType = true;
          } else {
            hasWithoutInitiatorType = true;
          }
        });
      });

      // Determine the type based on what we found
      if (hasWithInitiatorType && hasWithoutInitiatorType) {
        return { type: "both", hasFinalize: true, hasAccept: true };
      } else if (hasWithInitiatorType) {
        return { type: "accept", hasFinalize: false, hasAccept: true };
      } else if (hasWithoutInitiatorType) {
        return { type: "finalize", hasFinalize: true, hasAccept: false };
      } else {
        return { type: "finalize", hasFinalize: false, hasAccept: false };
      }
    },
    [onGoingData, user],
  );

  const normalizeTime = (time) => (time === "24:00" ? "23:59" : time);

  const getAllFinalizedConnectionIds = React.useCallback(
    (onGoingData, finalValues) => {
      const ids = [];
      if (!onGoingData?.matches) return ids;

      // Helper function to check if a slot is selected (same as in transformToPayload)
      const isSlotSelected = (
        selfSlotId,
        matchSlotId,
        connectId,
        companyName,
        matchIndex,
      ) => {
        return selectedBids.some((bid) => {
          // Match by uniqueId (format: selfSlotId-matchSlotId or selfSlotId-matchSlotId-matchIndex)
          const uniqueId1 = `${selfSlotId}-${matchSlotId}`;
          const uniqueId2 = `${selfSlotId}-${matchSlotId}-${matchIndex}`;

          // Also match by stable properties (in case IDs changed)
          const stableMatch =
            bid.selfRequisitionIndex == selfSlotId &&
            bid.consumerGeneratorconnectId == connectId &&
            bid.company === companyName;

          return (
            bid.uniqueId === uniqueId1 ||
            bid.uniqueId === uniqueId2 ||
            stableMatch
          );
        });
      };

      const processSlot = (
        slot,
        selfSlotId,
        connectId,
        companyName,
        matchIndex,
      ) => {
        if (
          slot.finalizedConnectionValues &&
          slot.finalizedConnectionValues.id !== undefined
        ) {
          // Normalize times
          const slotStartAt = slot.finalizedConnectionValues.slotStartAt
            ? normalizeTime(slot.finalizedConnectionValues.slotStartAt)
            : undefined;
          const slotEndAt = slot.finalizedConnectionValues.slotEndAt
            ? normalizeTime(slot.finalizedConnectionValues.slotEndAt)
            : undefined;

          // Check if this slot is selected using flexible matching
          if (
            isSlotSelected(
              selfSlotId,
              slot.id,
              connectId,
              companyName,
              matchIndex,
            )
          ) {
            ids.push(slot.finalizedConnectionValues.id);
          }
        }
      };

      if (!finalValues) {
        // Process all slots
        onGoingData.matches.forEach((match, matchIndex) => {
          [...(match.consumers || []), ...(match.generators || [])].forEach(
            (item) => {
              (item.matchSlots || []).forEach((slot) => {
                processSlot(
                  slot,
                  match.selfSlot.id,
                  item.connectId,
                  item.companyName,
                  matchIndex,
                );
              });
            },
          );
        });
      } else {
        // Process only selected slots
        onGoingData.matches.forEach((match, matchIndex) => {
          [...(match.consumers || []), ...(match.generators || [])].forEach(
            (item) => {
              (item.matchSlots || []).forEach((slot) => {
                processSlot(
                  slot,
                  match.selfSlot.id,
                  item.connectId,
                  item.companyName,
                  matchIndex,
                );
              });
            },
          );
        });
      }

      return ids;
    },
    [selectedBids],
  );

  const handleTermsSubmit = useCallback(
    async (finalValues) => {
      try {
        setTermsSubmitLoading(true);
        const finalizeTermsPayload = transformToPayload(
          finalValues,
          user,
          onGoingData,
          listingId,
          selectedBids,
        );

        const payloadType = isEditTerms
          ? { type: "finalize" }
          : checkPayloadType(finalValues);
        const acceptTermsPayload = getAllFinalizedConnectionIds(
          onGoingData[0],
          finalValues,
          selectedBids,
        );

        let finalPayload;

        if (payloadType.type === "both") {
          // Include both finalize and accept data
          finalPayload = {
            ...finalizeTermsPayload,
            connectionIds: acceptTermsPayload,
          };
        } else if (payloadType.type === "accept") {
          // Only include accept data
          finalPayload = {
            connectionIds: acceptTermsPayload,
            capacityId: listingId,
            userType: user,
          };
        } else if (payloadType.type === "finalize") {
          // Only include finalize data
          finalPayload = {
            ...finalizeTermsPayload,
          };
        }
        let res;
        if (isEditTerms) {
          res = await handleInitEditT_C(finalPayload);
        } else {
          res = await handleAcceptT_C(finalPayload);
        }

        if (res?.data?.code === 200) {
          setIsEditTerms(false);
          handleTermsClose();
          dispatch(
            fetchActiveConnection({
              curr: 0,
              capId: listingId,
              isTermsAccepted: null,
            }),
          );
          dispatch(fetchSelfCapacityById({ user, id: listingId }));
          if (isEditTerms) {
            SuccessMessage("Terms & Conditions edited successfully!");
          } else {
            SuccessMessage("Terms & Conditions accepted successfully!");
          }
          setSelectedBids([]);
          navigate("/dashboard");
        } else {
          ErrorMessage("Failed to accept Terms & Conditions");
        }
      } catch (error) {
        ErrorMessage("An error occurred while submitting");
      } finally {
        setConfirmOpen(false);
        setTermsSubmitLoading(false);
      }
    },
    [
      user,
      onGoingData,
      listingId,
      selectedBids,
      isEditTerms,
      checkPayloadType,
      getAllFinalizedConnectionIds,
      handleTermsClose,
      dispatch,
      navigate,
    ],
  );

  const handleWithdraw = useCallback(async () => {
    const finalizedIds = getAllContractIds(onGoingData);
    if (finalizedIds?.length === 0) {
      return ErrorMessage("Please Select Requisition(s)");
    }

    const payload = { contractIds: finalizedIds };

    try {
      setWithdrawLoading(true);
      const res = await withdrawTerms(payload);
      if (res?.data?.code === 200) {
        dispatch(
          fetchActiveConnection({
            curr: 0,
            capId: listingId,
            isTermsAccepted: null,
          }),
        );
        dispatch(fetchSelfCapacityById({ user, id: listingId }));
        setAcceptTermsModal(false);
        setConfirmWithdrawModalOpen(false);
        setSelectedBids([]);
        navigate("/dashboard");
      }
    } catch (error) {
    } finally {
      setWithdrawLoading(false);
      setRejectOpen(false);
      setIsEditTerms(false);
    }
  }, [onGoingData, dispatch, listingId, user, navigate]);

  const handleAcceptTermsSubmit = useCallback(
    async (e) => {
      const finalizedIds = getAllFinalizedConnectionIds(acceptTermsModalData);
      if (finalizedIds?.length === 0) {
        return ErrorMessage("Please Select Requisition(s)");
      }

      const payload = {
        connectionIds: finalizedIds,
        userType: user,
        capacityId: listingId,
      };

      try {
        setApproveTermsLoading(true);
        const res = await handleAcceptT_C(payload);
        if (res?.data?.code === 200) {
          dispatch(
            fetchActiveConnection({
              curr: 0,
              capId: listingId,
              isTermsAccepted: null,
            }),
          );
          dispatch(fetchSelfCapacity({ user }));
          handleAcceptTermsClose();
          SuccessMessage(res?.data?.message);
          navigate("/dashboard");
        }
      } catch (error) {
        ErrorMessage("An error occurred while accepting terms.");
      } finally {
        setApproveTermsLoading(false);
      }
    },
    [
      getAllFinalizedConnectionIds,
      acceptTermsModalData,
      user,
      listingId,
      dispatch,
      handleAcceptTermsClose,
      navigate,
    ],
  );

  const handleRejectAllTermsSubmit = useCallback(
    async (e, data) => {
      e.preventDefault();
      e.stopPropagation();

      const payload = {
        capacityId: listingId,
      };

      try {
        const res = await handleRejectAllT_C(payload);
        if (res?.data?.code === 200) {
          dispatch(
            fetchActiveConnection({
              curr: 0,
              capId: listingId,
              isTermsAccepted: null,
            }),
          );
          dispatch(fetchSelfCapacity({ user }));
          handleAcceptTermsClose();
          SuccessMessage(res?.data?.message);
          navigate("/dashboard");
        }
      } catch (error) {
        ErrorMessage("An error occurred while rejecting terms.");
      }
    },
    [user, listingId, dispatch, handleAcceptTermsClose, navigate],
  );

  const filterMatchesBySelectedBids = (data, selectedBids) => {
    if (!data?.matches || !selectedBids?.length) return [];

    // Create a Set for quick lookup using uniqueId instead of id
    const selectedUniqueIds = new Set(selectedBids.map((bid) => bid.uniqueId));

    return data.matches
      .map((match, matchIndex) => {
        // Filter consumers with matching uniqueId
        const filteredConsumers = (match.consumers || [])
          .map((consumer) => {
            const filteredMatchSlots = (consumer.matchSlots || []).filter(
              (slot) => {
                // Create the same uniqueId format used in the toggle functions
                const uniqueId = `${match.selfSlot.id}-${slot.id}-${matchIndex}`;
                return (
                  slot.finalizedConnectionValues &&
                  selectedUniqueIds.has(uniqueId)
                );
              },
            );
            if (filteredMatchSlots.length > 0) {
              return { ...consumer, matchSlots: filteredMatchSlots };
            }
            return null;
          })
          .filter(Boolean);

        // Filter generators with matching uniqueId
        const filteredGenerators = (match.generators || [])
          .map((generator) => {
            const filteredMatchSlots = (generator.matchSlots || []).filter(
              (slot) => {
                // Create the same uniqueId format used in the toggle functions
                const uniqueId = `${match.selfSlot.id}-${slot.id}-${matchIndex}`;
                return (
                  slot.finalizedConnectionValues &&
                  selectedUniqueIds.has(uniqueId)
                );
              },
            );
            if (filteredMatchSlots.length > 0) {
              return { ...generator, matchSlots: filteredMatchSlots };
            }
            return null;
          })
          .filter(Boolean);

        if (filteredConsumers.length > 0 || filteredGenerators.length > 0) {
          return {
            ...match,
            consumers: filteredConsumers,
            generators: filteredGenerators,
          };
        }
        return null;
      })
      .filter(Boolean);
  };

  const handleApproveT_C = useCallback(
    (e, data) => {
      e.preventDefault();
      e.stopPropagation();

      if (selectedBids.length > 0) {
        const filteredMatches = filterMatchesBySelectedBids(data, selectedBids);
        setAcceptTermsModalData({ ...data, matches: filteredMatches });
        // setTermsModal(true);
        setAcceptTermsModal(true);
      } else {
        toast.error("Please Select Requisition(s)");
      }
    },
    [selectedBids],
  );

  const handleEditT_C = useCallback(
    (e, data) => {
      e.preventDefault();
      e.stopPropagation();
      if (selectedBids.length > 0) {
        const filteredMatches = filterMatchesBySelectedBids(data, selectedBids);
        setEditTermsData({ ...data, matches: filteredMatches });
        setEditTermsModal(true);
      } else {
        toast.error("Please Select Requisition(s)");
      }
    },
    [selectedBids],
  );

  const handleTermsEditSubmit = async (finalValues) => {
    setTermsSubmitLoading(true);
    const finalizeTermsPayload = transformToPayload(
      finalValues,
      user,
      onGoingData,
      listingId,
      selectedBids,
    );
    const res = await handleInitEditT_C(finalizeTermsPayload);
    if (res?.data?.code === 200) {
      handleTermsClose();
      dispatch(
        fetchActiveConnection({
          curr: 0,
          capId: listingId,
          isTermsAccepted: null,
        }),
      );
      dispatch(fetchSelfCapacityById({ user, id: listingId }));
      SuccessMessage("Terms & Conditions accepted successfully!");
      navigate("/dashboard");
      setSelectedBids([]);
    } else {
      ErrorMessage("Failed to accept Terms & Conditions");
    }
  };

  const checkCounterpartiesHaveInitiatorType = (onGoingData, user) => {
    // Check if onGoingData exists and has matches
    if (
      !onGoingData ||
      !onGoingData.matches ||
      !Array.isArray(onGoingData.matches)
    ) {
      return {
        hasInitiatorType: false,
        totalCounterparties: 0,
        counterpartiesWithInitiatorType: 0,
        counterpartiesWithoutInitiatorType: 0,
        allHaveInitiatorType: false,
        someHaveInitiatorType: false,
        noneHaveInitiatorType: true,
        allAreGenerator: false,
        allAreConsumer: false,
        allAreBoth: false,
        details: [],
      };
    }

    // Determine counterparty key based on user type
    const counterpartyKey = user === "consumer" ? "generators" : "consumers";

    let totalCounterparties = 0;
    let counterpartiesWithInitiatorType = 0;
    let counterpartiesWithoutInitiatorType = 0;
    let generatorInitiatorCount = 0;
    let consumerInitiatorCount = 0;
    const details = [];

    // Iterate through all matches
    onGoingData.matches.forEach((match, matchIndex) => {
      const counterparties = match[counterpartyKey] || [];

      counterparties.forEach((counterparty, counterpartyIndex) => {
        totalCounterparties++;

        const hasInitiatorType =
          counterparty.hasOwnProperty("initiatorType") &&
          counterparty.initiatorType !== null &&
          counterparty.initiatorType !== undefined;

        if (hasInitiatorType) {
          counterpartiesWithInitiatorType++;

          // Count specific initiator types
          if (counterparty.initiatorType === "generator") {
            generatorInitiatorCount++;
          } else if (counterparty.initiatorType === "consumer") {
            consumerInitiatorCount++;
          }
        } else {
          counterpartiesWithoutInitiatorType++;
        }

        details.push({
          matchIndex,
          counterpartyIndex,
          connectId: counterparty.connectId,
          companyName: counterparty.companyName,
          hasInitiatorType,
          initiatorType: counterparty.initiatorType || null,
        });
      });
    });

    // Check if ALL counterparties have initiatorType
    const allHaveInitiatorType =
      totalCounterparties > 0 && counterpartiesWithoutInitiatorType === 0;

    // Determine if all initiatorTypes are of specific types
    // Only true if ALL counterparties have initiatorType AND they're all the same type
    const allAreGenerator =
      allHaveInitiatorType &&
      totalCounterparties > 0 &&
      generatorInitiatorCount === totalCounterparties &&
      consumerInitiatorCount === 0;

    const allAreConsumer =
      allHaveInitiatorType &&
      totalCounterparties > 0 &&
      consumerInitiatorCount === totalCounterparties &&
      generatorInitiatorCount === 0;

    const allAreBoth =
      allHaveInitiatorType &&
      generatorInitiatorCount > 0 &&
      consumerInitiatorCount > 0;

    return {
      hasInitiatorType: allHaveInitiatorType,
      allHaveInitiatorType,
      someHaveInitiatorType: counterpartiesWithInitiatorType > 0,
      noneHaveInitiatorType: counterpartiesWithInitiatorType === 0,
      allAreGenerator,
      allAreConsumer,
      allAreBoth,
      totalCounterparties,
      counterpartiesWithInitiatorType,
      counterpartiesWithoutInitiatorType,
      generatorInitiatorCount,
      consumerInitiatorCount,
      details,
    };
  };
  const result = checkCounterpartiesHaveInitiatorType(onGoingData[0], user);

  useEffect(() => {
    dispatch(fetchMainScreenActiveConnection({ capId: listingId }));
  }, [dispatch, listingId]);

  const mainScreenMatchData = useSelector(
    (state) => state?.CapacitySlice?.mainScreenActiveConnection,
  );

  /**
   * Checks all initiatorType in the data and sets the active subtab.
   * If all initiatorType are 'consumer' and user is 'generator', set to 'T&C Approval Requests'.
   * If all initiatorType are 'generator' and user is 'consumer', set to 'T&C Approval Requests'.
   * Otherwise, do not change the subtab.
   * @param {Array} data - The array of connection objects.
   * @param {string} user - The current user type ('consumer' or 'generator').
   * @param {Function} setActiveSubTab - The setter for active subtab.
   */
  const checkAndSetActiveSubTab = useCallback(
    (data, user, setActiveSubTab) => {
      if (hasApprovedAndPending) return;
      if (!Array.isArray(data) || !user) return;

      const allInitiatorTypes = data.map((item) => item.initiatorType);

      if (allInitiatorTypes.length === 0) {
        setActiveSubTab("T&C to initiate");
        return;
      }

      // If all initiatorType are 'consumer' and user is 'generator'
      if (
        allInitiatorTypes.every((type) => type === "consumer") &&
        user === "generator"
      ) {
        setActiveSubTab("T&C Approval Requests");
        return;
      }

      // If all initiatorType are 'generator' and user is 'consumer'
      if (
        allInitiatorTypes.every((type) => type === "generator") &&
        user === "consumer"
      ) {
        setActiveSubTab("T&C Approval Requests");
        return;
      }
    },
    [hasApprovedAndPending],
  );

  useEffect(() => {
    checkAndSetActiveSubTab(mainScreenMatchData, user, setActiveSubTab);
  }, [mainScreenMatchData, user, setActiveSubTab, checkAndSetActiveSubTab]);

  // Filtered data for modals
  const getFilteredDataForModal = useCallback(() => {
    if (selectedBids.length === 0) return [];
    const userDataKey =
      user === USER_TYPES.CONSUMER ? "generators" : "consumers";
    const normalizedData = Array.isArray(onGoingData)
      ? onGoingData
      : [onGoingData];
    return normalizedData
      ?.map((item) => {
        const filteredMatches = item.matches
          ?.map((match) => {
            const filteredCounterparties = match[userDataKey]
              ?.map((counterparty) => {
                const filteredMatchSlots = counterparty.matchSlots?.filter(
                  (slot) => {
                    const uniqueId = `${match.selfSlot.id}-${slot.id}`;
                    return requisition?.some(
                      (req) => req.uniqueId === uniqueId,
                    );
                  },
                );

                if (filteredMatchSlots && filteredMatchSlots.length > 0) {
                  return {
                    ...counterparty,
                    matchSlots: filteredMatchSlots,
                  };
                }
                return null;
              })
              .filter(Boolean);

            if (filteredCounterparties && filteredCounterparties.length > 0) {
              return {
                ...match,
                [userDataKey]: filteredCounterparties,
              };
            }
            return null;
          })
          .filter(Boolean);

        if (filteredMatches && filteredMatches.length > 0) {
          return {
            ...item,
            matches: filteredMatches,
          };
        }
        return null;
      })
      .filter(Boolean);
  }, [selectedBids, user, onGoingData, requisition]);

  const handleFinalizeT_c = useCallback(
    (e, data) => {
      e.preventDefault();
      e.stopPropagation();
      if (isEditTerms) {
        if (selectedBids?.length > 0) {
          setTermsId(data);
          setTermsModal(true);
        } else {
          toast.error("Please Select Requisition(s)");
        }
      } else {
        if (selectedBids?.length > 0 && getFilteredDataForModal().length > 0) {
          setTermsId(data);
          setTermsModal(true);
        } else {
          toast.error("Please Select Requisition(s)");
        }
      }
    },
    [getFilteredDataForModal, isEditTerms, selectedBids?.length],
  );

  // Helper functions
  const uniqueSelfCapacity = useMemo(
    () => getUniqueCapacities(mainScreenMatchData, user, activeListingStatus),
    [mainScreenMatchData, user, activeListingStatus],
  );

  const uniqueCounterCapacity = useMemo(
    () => getUniqueCounterCapacity(mainScreenMatchData, activeListingStatus),
    [mainScreenMatchData, activeListingStatus],
  );

  const isLoading = selfCapacityLoading || activeConnectionLoading;
  const hasNoData = onGoingData?.matches?.length === 0;

  const handleProceed = React.useCallback(async () => {
    // Function to get IDs where initiatorType matches user and status is PENDING
    const getFilteredIds = (data, currentUser) => {
      if (!Array.isArray(data)) return [];

      return data
        .filter(
          (item) =>
            item.initiatorType === currentUser && item.status === "PENDING",
        )
        .map((item) => item.id);
    };

    // Get the filtered IDs
    const filteredIds = getFilteredIds(mainScreenMatchData, user);

    if (filteredIds?.length === 0) {
      return ErrorMessage("Nothing to proceed");
    }
    let payload = {
      contractIds: filteredIds,
      capacityId: listingId,
    };

    try {
      setProceedToBilateralLoading(true);

      const res = await withdrawTerms(payload);
      if (res?.data?.code === 200) {
        dispatch(
          fetchActiveConnection({
            curr: 0,
            capId: listingId,
            isTermsAccepted: null,
          }),
        );
        dispatch(fetchMainScreenActiveConnection({ capId: listingId }));
        dispatch(fetchSelfCapacity({ user }));
        dispatch(fetchSelfCapacityById({ user, id: listingId }));
        setProceedToBilateralConfirm(false);
        setSelectedBids([]);
        setIsEditTerms(false);
      }
    } catch (error) {
    } finally {
      setProceedToBilateralLoading(false);
    }
  }, [dispatch, listingId, mainScreenMatchData, user]);

  const getBtn = React.useMemo(() => {
    if (
      activeListingStatus === "TERMS_INITIATED" &&
      result.allAreGenerator &&
      user === "consumer"
    ) {
      return [
        {
          label: ACTIONS.REJECTALL,
          action: (e) => setRejectAllTermsConfirm(true),
        },
        { label: ACTIONS.ACCEPT, action: handleApproveT_C },
      ];
    } else if (
      onGoingData[0]?.status === "PARTIAL_FINAL" &&
      user === "consumer"
    ) {
      return [
        {
          label: ACTIONS.EDITTERMS,
          action: (e) => {
            setIsEditTerms(true);
            handleFinalizeT_c(e, onGoingData[0]);
          },
        },
        {
          label: ACTIONS.PROCEED,
          action: (e) => {
            setProceedToBilateralConfirm(true);
          },
        },
      ];
    } else if (
      activeListingStatus === "TERMS_INITIATED" &&
      result.allAreConsumer &&
      user === "consumer"
    ) {
      return {
        label: ACTIONS.EDITTERMS,
        action: (e) => {
          setIsEditTerms(true);
          handleFinalizeT_c(e, onGoingData[0]);
        },
      };
    } else if (
      activeListingStatus === "TERMS_INITIATED" &&
      result.allAreConsumer &&
      user === "generator"
    ) {
      return [
        {
          label: ACTIONS.REJECTALL,
          action: () => setRejectAllTermsConfirm(true),
        },
        { label: ACTIONS.ACCEPT, action: handleApproveT_C },
      ];
    } else if (
      activeListingStatus === "TERMS_INITIATED" &&
      result.allAreGenerator &&
      user === "generator"
    ) {
      return {
        label: ACTIONS.EDITTERMS,
        action: (e) => {
          setIsEditTerms(true);
          handleFinalizeT_c(e, onGoingData[0]);
        },
      };
    } else if (
      activeListingStatus === "TERMS_INITIATED" &&
      result.someHaveInitiatorType &&
      user === "consumer"
    ) {
      return { label: ACTIONS.FINALIZE, action: handleFinalizeT_c };
    } else {
      if (
        result.allHaveInitiatorType &&
        result.allAreGenerator &&
        user !== "generator"
      ) {
        return { label: ACTIONS.ACCEPT, action: handleApproveT_C };
      } else {
        return { label: ACTIONS.FINALIZE, action: handleFinalizeT_c };
      }
    }
  }, [
    activeListingStatus,
    result.allAreGenerator,
    result.allAreConsumer,
    result.someHaveInitiatorType,
    result.allHaveInitiatorType,
    user,
    onGoingData,
    handleApproveT_C,
    handleFinalizeT_c,
  ]);

  useEffect(() => {
    if (
      getBtn.label === ACTIONS.EDITTERMS ||
      getBtn.label === ACTIONS.PROCEED
    ) {
      setIsEditTerms(true);
    } else {
      setIsEditTerms(false);
    }
  }, [getBtn]);

  // Render helpers
  const renderAccordionSummary = (item, index, isUnique = false) => {
    const data = isUnique ? onGoingData[0] : item;

    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "100%",
        }}
      >
        {(() => {
          const btnProps = getBtn;
          const buttons = Array.isArray(btnProps) ? btnProps : [btnProps];
          return buttons.map(({ label, action, disabled }, idx) => {
            return (
              <button
                key={label + idx}
                className={`display_flex_row ${
                  label === ACTIONS.WITHDRAW
                    ? Styles.withdrawBtn
                    : label === ACTIONS.WAITING
                      ? Styles.waitingBtn
                      : label === "Reject All"
                        ? Styles.withdrawBtn
                        : Styles.finalTcBtn
                }`}
                onClick={disabled ? undefined : (e) => action(e, data)}
                disabled={disabled}
                style={{
                  marginRight: 8,
                  cursor: disabled ? "not-allowed" : "pointer",
                }}
              >
                {label === ACTIONS.EDITTERMS ? "Edit T&C" : label}
              </button>
            );
          });
        })()}
      </div>
    );
  };

  const renderAccordionDetails = (data, isUnique = false) => {
    const isLedgerFinalTc =
      data[getRoleDataKey()]?.status === "CLOSED" ||
      (result.allHaveInitiatorType &&
        (((result.allAreConsumer ||
          result.allAreGenerator ||
          result.allAreBoth) &&
          user === "consumer") ||
          ((result.allAreConsumer ||
            result.allAreGenerator ||
            result.allAreBoth) &&
            user === "generator")));

    let aa =
      data[
        user === USER_TYPES.CONSUMER ? "capacityConsumer" : "capacityGenerator"
      ]?.status === "CLOSED";

    return (
      <AccordionDetails>
        <div className={Styles.accordionDetailsContainer_t2}>
          {isUnique || aa ? (
            <LedgerUploadDocs
              viewType={viewType}
              capacityId={capacityId}
              wholeData={onGoingData}
              data={uniqueCounterCapacity}
              setHideBucketFill={setHideBucketFill}
            />
          ) : isLedgerFinalTc ? (
            <LedgerFinalTc
              result={result}
              setTermsModal={setTermsModal}
              activeListingStatus={activeListingStatus}
              data={data}
              requisition={requisition}
              companyUpdate={companyUpdate}
              handleSelectedBids={handleSelectedBids}
              handleReq={handleReq}
              setHideBucketFill={setHideBucketFill}
              isEditTerms={isEditTerms}
              setIsEditTerms={setIsEditTerms}
            />
          ) : (
            <LegerViewType
              data={data?.matches}
              requisition={requisition}
              companyUpdate={companyUpdate}
              handleSelectedBids={handleSelectedBids}
              handleReq={handleReq}
              setHideBucketFill={setHideBucketFill}
            />
          )}
        </div>
      </AccordionDetails>
    );
  };

  const renderHeader = () => {
    return (
      <div className={Styles.headerContainer}>
        <div>
          {" "}
          <span className={Styles.Label}>
            EMD <div> {selfReq?.emd} (₹/kWh)</div>
          </span>
        </div>
        <div>
          {" "}
          <span className={Styles.Label}>
            Initial Margin <div>{selfReq?.initialMargin}%</div>
          </span>
        </div>
        <div>
          {" "}
          <span className={Styles.Label}>
            Listing Expiry Date & Time{" "}
            <div>
              {formatDate(selfReq?.listingExpDate)} - {selfReq?.listingExpTime}
            </div>
          </span>
        </div>
        <div>
          {" "}
          <span className={Styles.Label}>
            Terms Expiry Date & Time{" "}
            <div>
              {formatDate(selfReq?.termsExpDate)} - {selfReq?.termsExpTime}
            </div>
          </span>
        </div>
        {hasApprovedAndPending && activeListingStatus === "TERMS_INITIATED" ? (
          <>
            <div className={`display_flex_row ${Styles.sectionNamain}`}>
              {[
                {
                  name: "T&C to initiate",
                },
                {
                  name: "T&C Approval Requests",
                },
              ]?.map((ele) => (
                <div
                  style={{ cursor: "pointer" }}
                  type="button"
                  onClick={() => {
                    setActiveSubTab(ele?.name);
                    setCompanyUpdate([]);
                    setSelectedBids([]);
                    setRequsition([]);
                    setContractIds([]);
                  }}
                  role="button"
                  className={`display_flex_row_center ${Styles.activeSection_Nav_section}`}
                >
                  {ele?.name}
                </div>
              ))}
            </div>
          </>
        ) : (
          <div className={Styles.stepperContainer}>
            <div className={Styles.stepperHeader}>
              <span className={Styles.stepperStepText}>
                {activeListingStatus === "ONGOING"
                  ? "Step 3 of 3"
                  : activeListingStatus === "AGGREMENT_APPROVED"
                    ? "Step 2 of 3"
                    : "Step 1 of 3"}
              </span>
              <span className={Styles.stepperTitle}>
                {activeListingStatus === "ONGOING"
                  ? "Scheduling and Close"
                  : activeListingStatus === "AGGREMENT_APPROVED"
                    ? "Documentation"
                    : "Finalizing Terms & Conditions"}
              </span>
            </div>

            <div className={Styles.statusStepper}>
              {[
                {
                  completed: [
                    "TERMS_ACCEPTED",
                    "AGGREMENT_APPROVED",
                    "ONGOING",
                  ].includes(activeListingStatus),
                  active: [
                    "TERMS_INITIATED",
                    "APPROVED",
                    "TERMS_ACCEPTED",
                    "AGGREMENT_APPROVED",
                    "RECEIVED",
                    "PLACED",
                    "ONGOING",
                  ].includes(activeListingStatus),
                },
                {
                  completed: ["AGGREMENT_APPROVED", "ONGOING"].includes(
                    activeListingStatus,
                  ),
                  active: [
                    "TERMS_ACCEPTED",
                    "AGGREMENT_APPROVED",
                    "ONGOING",
                  ].includes(activeListingStatus),
                },
                {
                  completed: false,
                  active: activeListingStatus === "ONGOING",
                },
              ].map((step, idx) => (
                <span
                  key={idx}
                  className={`${Styles.stepCircle} ${
                    step.completed
                      ? Styles.completed
                      : step.active
                        ? Styles.active
                        : Styles.inactive
                  }`}
                >
                  {step.completed ? (
                    <span className={Styles.checkIcon}>&#10003;</span>
                  ) : (
                    <span
                      className={
                        step.active ? Styles.activeDot : Styles.inactiveDot
                      }
                    ></span>
                  )}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  function withdrawCase() {
    if (onGoingData[0]?.status === "PARTIAL_FINAL" && user === "consumer")
      return false;
    if (
      ((activeListingStatus === "TERMS_INITIATED" &&
        result.allAreGenerator &&
        user === "generator") ||
        (activeListingStatus === "TERMS_INITIATED" &&
          result.allAreConsumer &&
          user === "consumer")) &&
      mainScreenMatchData?.every(
        (item) =>
          item.initiatorType === "consumer" ||
          item.initiatorType === "generator",
      )
    ) {
      return true;
    }
    return false;
  }

  return (
    <>
      {/* Modals */}
      {termsModal && (
        <TermsCondition
          open={termsModal}
          handleClose={handleTermsClose}
          data={getFilteredDataForModal()}
          handleTermsSubmit={handleTermsSubmit}
          selectedBids={selectedBids}
          wholeData={onGoingData}
          loading={termsSubmitLoading}
          isEditTerms={isEditTerms}
        />
      )}

      {acceptTermsModal && (
        <AcceptTermsCondition
          open={acceptTermsModal}
          rejectOpen={rejectOpen}
          setRejectOpen={setRejectOpen}
          handleClose={handleAcceptTermsClose}
          data={acceptTermsModalData}
          handleTermsSubmit={handleAcceptTermsSubmit}
          confirmOpen={confirmOpen}
          setConfirmOpen={setConfirmOpen}
          wholeData={onGoingData}
          handleWithdraw={handleWithdraw}
          withdrawLoading={withdrawLoading}
          termsSubmitLoading={approveTermsLoading}
        />
      )}
      {editTermsModal && (
        <EditTermsConnection
          open={editTermsModal}
          handleClose={handleEditTermsClose}
          data={editTermsData}
          handleTermsSubmit={handleTermsEditSubmit}
          wholeData={onGoingData}
          loading={termsSubmitLoading}
        />
      )}

      <ConfirmationModal
        open={confirmWithdrawModalOpen}
        handleClose={() => setConfirmWithdrawModalOpen(false)}
        handleSubmit={() => handleWithdraw()}
        header={"Confirm Withdrawal of Terms"}
        data={
          "Withdrawal will cancel this transaction. Do you want to continue?"
        }
        loading={withdrawLoading}
      />

      <ConfirmationModal
        open={rejectAllTermsConfirm}
        handleClose={() => setRejectAllTermsConfirm(false)}
        handleSubmit={(e) => handleRejectAllTermsSubmit(e)}
        header={"Confirm Rejection of Terms"}
        data={
          "Declining will cancel this transaction. Do you want to continue?"
        }
        loading={withdrawLoading}
      />

      <ConfirmationModal
        open={proceedToBilateralConfirm}
        handleClose={() => setProceedToBilateralConfirm(false)}
        handleSubmit={() => handleProceed()}
        header={"Confirm Proceed to Bilateral"}
        data={
          "Proceeding to bilateral negotiations will automatically reject all pending negotiations currently in progress. This action is irreversible. Do you wish to continue?"
        }
        loading={proceedToBilateralLoading}
      />

      <div style={{ height: "100%" }}>
        {/* Header */}
        {!(viewType === "RA_TERMS" || viewType === "RA_ACTIVE") && (
          <div className="connectionNav">
            <DetailsHeader
              status={activeListingStatus}
              heading={self?.name}
              displayActive={displayActiveText}
              dispalyList={[
                {
                  name: "Connections",
                  url: listingId ? `/ledger?id=${listingId}` : "/ledger",
                },
                {
                  name: "Active Connection",
                  url: listingId ? `/bilateral?id=${listingId}` : "/bilateral",
                },
                {
                  name: "Ongoing Connection",
                  url: listingId ? `/ongoing?id=${listingId}` : "/ongoing",
                },
              ]}
            />
          </div>
        )}

        {/* Main Content */}
        <div className={`${Styles.ledgerPageDashboardActiveMain}`}>
          {!(viewType === "RA_TERMS" || viewType === "RA_ACTIVE") &&
            renderHeader()}
          <TableHeader
            matchView={matchView}
            result={result}
            user={user}
            status={activeListingStatus}
            data={onGoingData}
            isInitiator={onGoingData?.[roleDataKey]?.initiatorType === user}
            hideBucketFill={hideBucketFill}
          />

          {isLoading ? (
            <>
              <CardLoader />
              <CardLoader />
            </>
          ) : hasNoData ? (
            <div style={{ position: "relative" }}>
              <EmptyState />
            </div>
          ) : (
            <>
              <div className={Styles.accordionSummaryContainer}>
                {activeListingStatus === "TERMS_ACCEPTED" ||
                activeListingStatus === "ONGOING" ? (
                  // Final status - show unique capacities
                  uniqueSelfCapacity?.map((item, i) => {
                    return (
                      <Accordion
                        style={{
                          backgroundColor: "#f3f5f8",
                        }}
                        key={i}
                        expanded={expanded === i}
                        onChange={() => handleAccordionChange(i)}
                      >
                        {renderAccordionDetails(item, true)}
                      </Accordion>
                    );
                  })
                ) : onGoingData?.matches ? (
                  // After T&C initialization
                  <Accordion
                    style={{
                      backgroundColor: "#f3f5f8",
                    }}
                    expanded={true}
                    onChange={() => handleAccordionChange(0)}
                  >
                    {renderAccordionDetails(onGoingData)}
                  </Accordion>
                ) : (
                  // Before T&C initialization
                  onGoingData?.map((elem, index) => (
                    <div
                      key={index}
                      className={
                        expanded === index
                          ? Styles.accordionSummaryContainer
                          : ""
                      }
                    >
                      <Accordion
                        style={{
                          backgroundColor: "#f3f5f8",
                        }}
                        expanded={expanded === index}
                        onChange={() => handleAccordionChange(index)}
                      >
                        {renderAccordionDetails(elem)}
                      </Accordion>
                    </div>
                  ))
                )}
              </div>
            </>
          )}

          {!hasNoData && (
            <>
              {onGoingData[0]?.status === "PARTIAL_FINAL" &&
              user === "generator" ? null : (
                <>
                  {onGoingData?.[roleDataKey]?.initiatorType &&
                  onGoingData?.[roleDataKey]?.status ===
                    "PARTIAL_FINAL" ? null : (
                    <>
                      {(activeListingStatus === "TERMS_INITIATED" ||
                        activeListingStatus === "PLACED" ||
                        activeListingStatus === "RECEIVED" ||
                        activeListingStatus === "APPROVED") && (
                        <div
                          className={
                            Styles.ledgerPageDashboardActiveMainFinalizeBtn
                          }
                        >
                          {activeListingStatus === "TERMS_ACCEPTED"
                            ? uniqueSelfCapacity?.map((item, i) =>
                                renderAccordionSummary(item, i, true),
                              )
                            : onGoingData?.matches
                              ? renderAccordionSummary(onGoingData, 0)
                              : onGoingData?.map((elem, index) =>
                                  renderAccordionSummary(elem, index),
                                )}
                        </div>
                      )}
                    </>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default LedgerPageDashboardActive;
